import {
    AxiosCartError,
    Cart,
    CartError,
    CartErrorTypes,
    CartMessage,
    ConsentTemplate,
    ContractType,
    CustomerContractOption,
    DisneyPlan,
    DocumentType,
    NetflixPlan,
    OrderStatus,
    Price,
    StockLevelStatuses,
    UserAuthState,
} from '@telialt/models/src/EshopModel';

import { RATE_PLAN_NAME_MANO_200 } from './eshopConstants';
import {
    isEntryWithDevice,
    isEntryWithDisney,
    isEntryWithESimCard,
    isEntryWithGift,
    isEntryWithNetflix,
    isEntryWithRatePlan,
    isEntryWithSimCard,
    isEntryWithUpsell,
} from './eshopTypeGuards';

export const selectFirstPurchasable = (cart: Cart) => cart?.entries?.find(isEntryWithDevice);
export const selectUpsellEntry = (cart: Cart) => cart?.entries?.find(isEntryWithUpsell);
export const selectUpsellEntries = (cart: Cart) => cart?.entries?.filter(isEntryWithUpsell);
export const selectNetflixEntry = (cart: Cart) => cart?.entries?.find(isEntryWithNetflix);
export const selectDisneyEntry = (cart: Cart) => cart?.entries?.find(isEntryWithDisney);
export const selectGiftEntries = (cart: Cart) => cart?.entries?.filter(isEntryWithGift);
export const selectGiftEntry = (cart: Cart) => cart?.entries?.find(isEntryWithGift);
export const selectGiftInCart = (cart: Cart) => selectGiftEntry(cart)?.product;
export const selectProductInCart = (cart: Cart) => selectFirstPurchasable(cart)?.product;
export const selectProductInCartCode = (cart: Cart) => selectProductInCart(cart)?.code;
export const selectProductInCartScalaId = (cart: Cart) => selectProductInCart(cart)?.scalaId;
export const selectProductInCartName = (cart: Cart) => selectProductInCart(cart)?.name;
export const selectProductStock = (cart: Cart) => selectProductInCart(cart)?.stock;
export const selectIsProductOutOfStock = (cart: Cart) =>
    selectProductStock(cart)?.stockLevelStatus === StockLevelStatuses.NoStock;
export const selectProductInCartCategory = (cart: Cart) => selectProductInCart(cart)?.category;
export const selectProductSpecialOffers = (cart: Cart) => selectProductInCart(cart)?.productSpecialOffers;
export const selectLeasingPlan = (cart: Cart) => cart?.leasingPlan;
export const selectInitialPayment = (cart: Cart) => cart?.selectedInitialPayment;
export const selectInitialPaymentOption = (cart: Cart) => cart?.selectedInitialPaymentOption;
export const selectMaxInitialPayment = (cart: Cart) => cart?.maxInitialPayment;
export const selectLeasingPlanCode = (cart: Cart) => selectLeasingPlan(cart)?.code;
export const selectLeasingPlanDuration = (cart: Cart) => selectLeasingPlan(cart)?.durationInMonths;
export const selectInsuranceFreePeriod = (cart: Cart) => cart?.freePeriodOfInsurancePayment;
export const selectAppliedVoucher = (cart: Cart) => cart?.appliedVouchers?.[0];
export const selectHasAppliedVoucher = (cart: Cart) => cart?.appliedVouchers?.length > 0;
export const selectRatePlanEntry = (cart: Cart) => cart?.entries?.find(isEntryWithRatePlan);
export const selectRatePlanLegalTerms = (cart: Cart) => selectRatePlanEntry(cart)?.product?.legalText;
export const selectRatePlanVasLegalTerms = (cart: Cart) => selectRatePlanEntry(cart)?.product?.vasLegalText;
export const selectRatePlanCode = (cart: Cart) => selectRatePlanEntry(cart)?.product?.code;
export const selectRatePlanServiceUoId = (cart: Cart) => selectRatePlanEntry(cart)?.serviceUoId;
export const selectRentPlan = (cart: Cart) => selectFirstPurchasable(cart)?.rentingPlanEntry;
export const selectSimEntry = (cart: Cart) => cart?.entries?.find(isEntryWithSimCard);
export const selectEsimEntry = (cart: Cart) => cart?.entries?.find(isEntryWithESimCard);
export const selectMobilityRatePlanType = (cart: Cart) => selectRatePlanEntry(cart)?.product?.mobilityRatePlanType;
export const selectOrderPromotions = (cart: Cart) => cart?.potentialOrderPromotions;
export const selectPossibleLeasingPlans = (cart: Cart) => cart?.possibleLeasingPlans;
export const selectPossibleRatePlans = (cart: Cart) => cart?.possibleRatePlans;
export const selectPossibleSapRatePlans = (cart: Cart) => cart?.possibleSapRatePlans;
export const selectHasPossibleSapRatePlans = (cart: Cart) => cart?.hasPossibleSapRatePlans;
export const selectPossibleServiceFeatures = (cart: Cart) => cart?.features;
export const selectBundleDiscountAvailable = (cart: Cart) => cart?.bundleDiscountAvailable;
export const selectVoucherAvailable = (cart: Cart) => cart?.voucherAvailable;
export const selectIsSapRatePlanOffersActive = (cart: Cart) => !!selectPossibleSapRatePlans(cart);
export const selectHasMultipleContractTypesAvailable = (cart: Cart) => cart?.possibleContractTypes?.length > 1;
export const selectHasWithPlanContractType = (cart: Cart) =>
    cart?.possibleContractTypes?.includes(ContractType.WithPlan);
export const selectHasWithoutPlanContractType = (cart: Cart) =>
    cart?.possibleContractTypes?.includes(ContractType.WithoutPlan);
export const selectLeasingPlansAvailability = (cart: Cart) => cart?.leasingPlansAvailability;
export const selectLeasingPlansAvailabilityWithoutPlan = (cart: Cart) => cart?.leasingPlansAvailabilityWithoutPlan;
export const selectConsentTemplate = (cart: Cart) =>
    cart?.consentTemplates?.find((item) => item.type === CartMessage.TermsOfUse);
export const selectConsentTemplateMessage = (cart: Cart) => selectConsentTemplate(cart)?.message;
export const selectAllConsentTemplates = (cart: Cart) => cart?.consentTemplates || [];
export const selectAllConsentTemplatesFlat = (cart: Cart) =>
    selectAllConsentTemplates(cart).reduce<ConsentTemplate[]>((acc, consent) => {
        return consent.consentTemplates ? [...acc, ...consent.consentTemplates] : [...acc, consent];
    }, []);
export const selectCommitment = (cart: Cart) => cart?.commitment;
export const selectSubscriptionOrder = (cart: Cart) => cart?.subscriptionOrderList?.[0];
export const selectNboBonusText = (cart: Cart) => selectRatePlanEntry(cart)?.nboBonusText;
export const selectPlanCharacteristics = (cart: Cart) => selectRatePlanEntry(cart)?.product?.planCharacteristics;
export const selectGuestCheckoutPossible = (cart: Cart) => cart?.guestCheckoutPossible === true;

// Prices:
export const selectProductTotalPriceByPaymentMethod = (cart: Cart): Price | undefined => {
    const isFullPricePayment = selectIsFullPricePayment(cart); // ToDo: for B2B ensure it neglects buying by Rent also
    const productEntry = selectFirstPurchasable(cart);

    return isFullPricePayment ? productEntry?.totalPrice : productEntry?.totalFullPriceWithDiscount;
};
export const selectProductOldTotalPrice = (cart: Cart): Price | undefined => {
    const standardPrice = selectProductInCart(cart)?.standardPrice;
    const basePrice = selectProductBasePrice(cart);
    const oldPrice = standardPrice || basePrice;
    const currentPrice = selectProductTotalPriceByPaymentMethod(cart);

    if (!oldPrice?.value) {
        return undefined;
    }

    if (!currentPrice?.value) {
        return oldPrice;
    }

    return oldPrice?.value > currentPrice?.value ? oldPrice : undefined;
};
export const selectProductTotalPriceWithDiscount = (cart: Cart) =>
    selectFirstPurchasable(cart)?.totalFullPriceWithDiscount;
export const selectProductBasePrice = (cart: Cart) => selectFirstPurchasable(cart)?.basePrice;
export const selectDiscountPrice = (cart: Cart) => selectProductInCart(cart)?.discountPrice;
export const selectLeasingDiscountPrice = (cart: Cart) => selectLeasingPlan(cart)?.productOfferDiscount;
export const selectTotalMonthlyPrice = (cart: Cart) => cart?.totalMonthlyPrice;
export const selectTotalMonthlyPriceWithoutRatePlan = (cart: Cart) => cart?.totalMonthlyPriceWithoutRatePlan;
export const selectTotalMonthlyPriceWithTax = (cart: Cart) => cart?.totalMonthlyPriceWithTax;
export const selectInsuranceMonthlyPrice = (cart: Cart) => cart?.insuranceMonthlyPrice;
export const selectNetflixData = (cart: Cart) => {
    const entry = selectNetflixEntry(cart);
    if (!entry) {
        return null;
    }

    const plan = entry.product?.code as NetflixPlan;
    const premiumPrice = cart?.netflixPremiumMonthlyPrice;

    return { plan, premiumPrice };
};
export const selectDisneyData = (cart: Cart) => {
    const entry = selectDisneyEntry(cart);
    if (!entry) {
        return null;
    }

    const plan = entry.product?.code as DisneyPlan;
    const premiumPrice = cart?.disneyPremiumMonthlyPrice;

    return { plan, premiumPrice };
};
export const selectTotalPrice = (cart: Cart) => cart?.totalPrice;
export const selectTotalPriceIsZero = (cart: Cart) => selectTotalPrice(cart)?.value === 0;
export const selectTotalPriceWithTaxIsZero = (cart: Cart) => selectTotalPriceWithTax(cart)?.value === 0;
export const selectTotalMonthlyPriceIsZero = (cart: Cart) => selectTotalMonthlyPrice(cart)?.value === 0;
export const selectTotalMonthlyPriceWithTaxIsZero = (cart: Cart) => selectTotalMonthlyPriceWithTax(cart)?.value === 0;
export const selectTotalPriceWithTax = (cart: Cart) => cart?.totalPriceWithTax;
export const selectTotalPriceWithoutRatePlan = (cart: Cart) => cart?.totalPriceWithoutRatePlan;
export const selectLeasingPlanPrice = (cart: Cart) => selectFirstPurchasable(cart)?.monthlyPrice;
export const selectedInsuranceFranchisePrice = (cart: Cart) => cart?.insuranceFranchisePrice;
export const selectTotalDiscounts = (cart: Cart) => cart?.totalDiscounts;
export const selectDeliveryCost = (cart: Cart) => cart?.deliveryCost;
export const selectDeliveryCostWithTax = (cart: Cart) => cart?.deliveryCostWithTax;
export const selectCouponsDiscounts = (cart: Cart) => cart?.couponsDiscounts;
export const selectSAPOfferCode = (cart: Cart) => cart?.sapOfferCode;
export const selectIsSAPOffer = (cart: Cart) => Boolean(selectSAPOfferCode(cart));

// Native boolean flags:
export const selectWithInsurance = (cart: Cart) => cart?.withInsurance;
export const selectExtend = (cart: Cart) => cart?.extend;
export const selectIsFromPublicWeb = (cart: Cart) => cart?.createdFromPublicWeb;
export const selectIsInsurancePossible = (cart: Cart) => selectProductInCart(cart)?.insurancePossible;

// Derived boolean flags:
export const selectIsCartFresh = (cart: Cart) => cart != null && !cart?.entries?.length;
export const selectIsCartAnonymous = (cart: Cart) => cart?.user?.uid === UserAuthState.Anonymous;
export const selectIsPreviouslyCloned = (cart: Cart) => Boolean(cart?.generatedUrlType);
export const selectIsFullPricePayment = (cart: Cart) => !selectLeasingPlan(cart);
export const selectIsWithUpsell = (cart: Cart) => selectUpsellEntries(cart)?.length > 0;
export const selectIsWithGifts = (cart: Cart) => selectGiftEntries(cart)?.length > 0;
export const selectIsWithRatePlan = (cart: Cart) => !!selectRatePlanEntry(cart);
export const selectIsWithSim = (cart: Cart) => !!selectSimEntry(cart);
export const selectIsWithEsim = (cart: Cart) => !!selectEsimEntry(cart);
export const selectIsPlanOrderOnly = (cart: Cart) => selectHasRatePlan(cart) && !selectHasDevice(cart);
export const selectHasDevice = (cart: Cart) => cart?.entries?.some(isEntryWithDevice);
export const selectHasRatePlan = (cart: Cart) => cart?.entries?.some(isEntryWithRatePlan);
export const selectHasRentPlan = (cart: Cart) => Boolean(selectRentPlan(cart));
export const selectHasInstallments = (cart: Cart) =>
    Boolean(selectLeasingPlan(cart) || selectHasRatePlan(cart) || selectHasRentPlan(cart));
export const selectHasRatePlanMano200 = (cart: Cart) =>
    selectRatePlanEntry(cart)?.product?.name === RATE_PLAN_NAME_MANO_200;

export const selectIsFullPriceFormFilled = (cart: Cart) =>
    Boolean(
        cart?.customerContractOption === CustomerContractOption.NoContract &&
        cart?.contactEmail &&
        cart?.contactName &&
        cart?.contactPhone,
    );
export const selectIsExtendSAPFormFilled = (cart: Cart) =>
    Boolean(
        (cart?.customerContractOption === CustomerContractOption.ExtendSAP ||
            cart?.customerContractOption === CustomerContractOption.NoContractSAP) &&
        cart?.contactEmail &&
        cart?.contactName &&
        cart?.contactPhone,
    );
export const selectIsExtendPlanFormFilled = (cart: Cart) =>
    cart?.customerContractOption === CustomerContractOption.Existing && !!cart?.migratePhoneNumber;
export const selectIsNewPlanFormFilled = (cart: Cart) =>
    Boolean(cart?.customerContractOption === CustomerContractOption.New && cart?.contactPhone && cart?.contactEmail);
export const selectIsTransferPlanFormFilled = (cart: Cart) =>
    Boolean(
        cart?.customerContractOption === CustomerContractOption.Migrate &&
        cart?.migratePhoneNumber &&
        cart?.migrateTelcoOperator?.code,
    );
export const selectIsClientDataStepComplete = (cart: Cart) =>
    selectIsFullPriceFormFilled(cart) ||
    selectIsExtendPlanFormFilled(cart) ||
    selectIsNewPlanFormFilled(cart) ||
    selectIsTransferPlanFormFilled(cart) ||
    selectIsExtendSAPFormFilled(cart);

export const selectIsDeliveryStepComplete = (cart: Cart) => !!cart?.deliveryAddress && !!cart?.deliveryMode;
export const selectIsDeliveryStepCompleteB2B = (cart: Cart) =>
    !!cart?.deliveryAddress && !!cart?.deliveryMode && !!cart?.contactName;

export const selectIsVatConfigInitialized = (cart: Cart) =>
    (selectIsRegisteredVatPayer(cart) === true && selectIsBudgetaryInstitution(cart) != null) ||
    selectIsRegisteredVatPayer(cart) === false;
export const selectIsRegisteredVatPayer = (cart: Cart) => cart?.registeredVatPayer;
export const selectIsBudgetaryInstitution = (cart: Cart) => cart?.budgetaryInstitution;
export const selectCompanyName = (cart: Cart) => cart?.companyName;
export const selectCompanyCode = (cart: Cart) => cart?.companyCode;
export const selectIsCompanyDataFilled = (cart: Cart) => Boolean(selectCompanyName(cart) && selectCompanyCode(cart));

export const selectRentingPlanVASProducts = (cart: Cart) => {
    const rentPlan = selectRentPlan(cart);
    if (!rentPlan) {
        return undefined;
    }

    const planKey = rentPlan.rentingPeriod;
    const planFromPossibleRentingPlans = cart.possibleRentingPlans.find((p) => p.rentingPeriod === planKey);

    return planFromPossibleRentingPlans?.vasProducts;
};

export const selectOrderAgreementDocument = (cart: Cart) =>
    cart?.orderDocuments?.find((s) => s?.documentType === DocumentType.OrderAgreement);
export const selectSodraDocument = (cart: Cart) =>
    cart?.orderDocuments?.find((s) => s?.documentType === DocumentType.SodraDocument);

export const selectOrderAgreementDocumentId = (cart: Cart) => selectOrderAgreementDocument(cart)?.documentId;

export const selectIsLocalOutOfStockError = (error: AxiosCartError) =>
    error?.response?.data?.errors?.[0]?.type === CartErrorTypes.OutOfStockLocalReservation;

export const selectIsAnyOfOutOfStockErrors = <Err extends { type: CartErrorTypes } = CartError>(error: Err) =>
    [
        CartErrorTypes.OutOfStock,
        CartErrorTypes.OutOfStockIlm,
        CartErrorTypes.OutOfStockColt,
        CartErrorTypes.OutOfStockLocalReservation,
        CartErrorTypes.CartBookingIlmError,
        CartErrorTypes.CartBookingColtError,
    ].includes(error?.type);

export const selectOrderCreditCheckErrorMessageKey = (cart: Cart) => cart?.creditCheckResult?.errorMessageKey;

export const selectHasCreditCheckFailedNotFinal = (cart: Cart) => cart?.status === OrderStatus.InitCreditCheckFailed;

export const selectHasCreditCheckFailedFinal = (cart: Cart) => cart?.status === OrderStatus.InitCreditCheckFailedFinal;

export const selectHasCreditCheckFailed = (cart: Cart) =>
    selectHasCreditCheckFailedNotFinal(cart) || selectHasCreditCheckFailedFinal(cart);

export const selectIsPaperlessOn = (cart: Cart) => !!cart?.isPaperlessOn;

export const selectIsEsimAvailable = (cart: Cart): boolean => Boolean(cart?.esimAvailable);
export const selectIsEsimOnly = (cart: Cart) => Boolean(cart?.esimOnly);

export const selectEsimInfo = (cart: Cart) => cart?.esimInfo;

export const selectFailureReason = (cart: Cart) => cart?.failureReason;

export const selectPopPaymentStatus = (cart: Cart) => cart?.popPaymentInfo?.status;

export const selectExtendedWarranties = (cart: Cart) => selectProductInCart(cart)?.extendedWarranties || [];

export const selectManufacturerWarranty = (cart: Cart) => selectProductInCart(cart)?.manufacturerWarranty;

export const selectHasNoDelivery = (cart: Cart) =>
    selectHasRatePlan(cart) &&
    !selectHasDevice(cart) &&
    !selectSimEntry(cart) &&
    !selectIsWithUpsell(cart) &&
    !selectIsWithGifts(cart);

export const selectAvailableServicesType = (cart: Cart) => cart?.availableServicesType;

export const selectIsMano200Available = (cart: Cart) => !!cart?.mano200Available;
